import { Injectable } from '@angular/core';
import { iGdprAcceptation, iClient, iRole, iPortalType, iUserStatus, iPortalMember, iPortal, iUser } from '../../interfaces/user';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ServerSideRenderingService } from '@coreservices/server-side-rendering.service';
import { MobileAppService } from '@coreservices/mobile-app.service';
import { TranslateService } from '@ngx-translate/core';

export enum registrationType{
	single = 1,
	double = 2
}

@Injectable()
export class UserService {

  constructor(private _cookieService: CookieService,
              private _serverSideRenderingService: ServerSideRenderingService,
              private _translateService: TranslateService) { }

  protected CLIENT: iPortalMember | null = null;
  public gdprAcceptation: iGdprAcceptation;
  protected PORTAL: iPortal | null = null;
  protected TOKEN_PAIR: { access_token: string, refresh_token: string } = null;
  fullPortal: iPortal;

  roles: iRole[] = [];
  portalTypes: iPortalType[] = []
  statuses: iUserStatus[] = []

  private trialPortalTime = new BehaviorSubject<number>(null);
  activatedUser = new BehaviorSubject<iPortalMember>(null);

  /*****************************************************  GETTERS  ****************************************************/

  getRole(): any {
    return this.roles.filter(role => role.id === this.client?.role_id)[0]?.name;
  }

  getPortalId(): number {
    return this.portal.id;
  }

  getRoleById(roleId: number): string {
    return this.roles.filter(role => role.id === roleId)[0]['name'];
  }

  getStatusById(id: number): string {
    return this.statuses.filter(role => role.id === id)[0]['name'];
  }

  getPortalType(): string {
    return this.portalTypes.filter(role => role.id === this.portal?.type_id)[0]?.name;
  }

  getPortalInfo(): iPortal {
    return this.fullPortal;
  }

  getUserName( creator , threads_anonym_posting_is_enabled: boolean,
     display_name_setting_code: string , is_anonym = false ): string {
    let userName = ''
    if(threads_anonym_posting_is_enabled && is_anonym){
        userName = this._translateService.instant('COMMUNITY_PAGE.anonymous_account');
    }
    if(!threads_anonym_posting_is_enabled || !is_anonym){
      switch(display_name_setting_code){
        case 'first_and_last_name': {
          if(creator?.first_name || creator?.last_name){
            userName = creator?.first_name + ' ' +  creator?.last_name
          }else{
             userName = this._translateService.instant('COMMON.community.community_member')
          }
          break;
        }
        case 'only_first_name':  {
          if(creator?.first_name){
            userName = creator?.first_name
          }else{
             userName = this._translateService.instant('COMMON.community.community_member')
          }
          break;
        }
        case 'custom_name':  {
          if(creator?.nickname){
            userName = creator?.nickname
          }else if(creator?.first_name || creator?.last_name){
            userName = creator?.first_name + ' ' +  creator?.last_name
          }else{
             userName = this._translateService.instant('COMMON.community.community_member')
          }
          break;
        }
      }
    }
    return userName
}

  get client(): iPortalMember {
    if (this._serverSideRenderingService.isBrowser) {
      if (this.CLIENT) {
        return this.CLIENT;
      } else if (localStorage.getItem('client_new')) {
        return JSON.parse(localStorage.getItem('client_new'));
      } else {
        return null;
      }
    }
  }

  get portal(): iPortal {
    if (this._serverSideRenderingService.isBrowser) {
      if (!!this.PORTAL) {
        return this.PORTAL;
      } else if (localStorage.getItem('portal_new')) {
        return JSON.parse(localStorage.getItem('portal_new'));
      } else {
        return null;
      }
    }
  }

  get getGdprAcceptation(): iGdprAcceptation {
    if (this._serverSideRenderingService.isBrowser) {
      if (JSON.parse(localStorage.getItem('gdpr_acceptation'))) {
        return JSON.parse(localStorage.getItem('gdpr_acceptation'));
      } else {
        return null;
      }
    }
  }

  get token(): string | null {
    if (this._serverSideRenderingService.isBrowser) {
      const searchParams = new URLSearchParams(window.location.search);

      // Prioritize 'hash', fallback to 'hilfe'
      // const tokenParam = searchParams.get('hash') || searchParams.get('hilfe');

      // if (tokenParam) {
      //   return tokenParam;
      // }
      if (this.TOKEN_PAIR) {
        return this.TOKEN_PAIR.access_token;
      } else if (localStorage.getItem('tokenPair_new')) {
        return JSON.parse(localStorage.getItem('tokenPair_new')).access_token;
      } else {
        return null;
      }
    }

    return null;
  }

  get refreshToken(): string | null {
    if (this.TOKEN_PAIR) {
      return this.TOKEN_PAIR.refresh_token;
    } else if (localStorage.getItem('tokenPair_new')) {
      return JSON.parse(localStorage.getItem('tokenPair_new')).refresh_token;
    } else {
      return null;
    }
  }

  get portal_member_id(): number | null {
    if (this._serverSideRenderingService.isBrowser) {
      if (this.CLIENT) {
        return this.CLIENT.id;
      } else if (localStorage.getItem('client_new')) {
        return JSON.parse(localStorage.getItem('client_new')).id;
      } else {
        return null;
      }
    }
  }

  get portal_id(): number | null {
    if (this._serverSideRenderingService.isBrowser) {
      if (this.CLIENT) {
        return this.portal.id;
      } else if (localStorage.getItem('portal_new')) {
        return JSON.parse(localStorage.getItem('portal_new')).id;
      } else {
        return null;
      }
    }
  }

  get portal_lang_code(): string {
    if (!this.client) return;

    if(this.portal.language_id === 1) {
      return 'en';
    } else if (this.portal.language_id === 2) {
      return 'de';
    } else if (this.portal.language_id === 3) {
      return 'sk';
    } else if (this.portal.language_id === 4) {
      return 'hu';
    } else if (this.portal.language_id === 5) {
      return 'mk';
    } else if (this.portal.language_id === 6) {
      return 'cz';
    }
  }

  getTrialPortalTime(): Observable<number | null> {
    return this.trialPortalTime;
  }

  /*****************************************************  SETTERS  ****************************************************/

  setTrialPortalTime(trialTime: number): void {
    this.trialPortalTime.next(trialTime);
  }

  setClient(clientObj: iClient, remember: boolean = true): void {
    if(clientObj[0]?.verification) {
      clientObj[0].verification = clientObj[0].verification[0];
    } else if(clientObj?.['verification']) {
      clientObj['verification'] = clientObj['verification'];
    }
    this.CLIENT = clientObj[0] ? clientObj[0] : clientObj;
    if (remember) localStorage.setItem('client_new', JSON.stringify(this.CLIENT));
    this.setCookie();

    MobileAppService.setIsMobileApp();
  }

  setPortal(portal: iPortal): void {
    if ((portal as any)?.length === 0) {
      this.PORTAL = null;
    }
    else if(portal[0]) {
      this.fullPortal = portal[0];
      this.PORTAL = portal[0];
    }  else {
      this.PORTAL = portal;
    }
    this.setTrialPortalTime(this.PORTAL.trial_ended_at);
    localStorage.setItem('portal_new', JSON.stringify(this.PORTAL));
  }

  setCookie(): void {
    if (this.getRole() === 'customer') this.deleteCookie();
    if (this.getRole() === 'content_creator') {
      const date: any = new Date(Date.now() + (24 * 60 * 60 * 1000));
      if (!environment.production) { // for local test - do not remove
        this._cookieService.set('abo_hash', this.token, date, '/', 'abobot.loc');
        this._cookieService.set('abo_domain', `${this.portal.domain}.loc:4200`, date, '/', 'abobot.loc');
      } else {
        this._cookieService.set('abo_hash', this.token, date, '/', 'mentortools.com', true);
        this._cookieService.set('abo_domain', `${this.portal.domain}`, date, '/', 'mentortools.com', true);
      }
    }
  }

  setGdprAcceptation(isAccept: boolean, time: number, isMarketing: boolean, isStatistics: boolean): void {
    this.gdprAcceptation = {is_accept: isAccept, accepted_at_epoch: time, is_marketing: isMarketing, is_statistics: isStatistics};
    localStorage.setItem('gdpr_acceptation', JSON.stringify(this.gdprAcceptation));
  }

  setRoles(roles: iRole[]): void {
    this.roles = roles;
  }

  setStatuses(statuses: iUserStatus[]): void {
    this.statuses = statuses;
  }

  setPortalTypes(types: iPortalType[]): void {
    this.portalTypes = types;
  }

  setTokenPair(tokenPair: { access_token: string, refresh_token: string }): Observable<any> {
    this.TOKEN_PAIR = tokenPair;
    localStorage.setItem('tokenPair_new', JSON.stringify(tokenPair));
    return of(true)
  }

  /*****************************************************  ACTIONS  ****************************************************/

  /* Clean current user object in memory */
  deleteClient(): void {
    if (this.getRole() === 'content_creator') this.deleteCookie();
    this.CLIENT = null;
    this.PORTAL = null;
    this.TOKEN_PAIR = null;
    if (this._serverSideRenderingService.isBrowser) {
      sessionStorage.removeItem('inactiveCCAlertWasClosed');
      localStorage.removeItem('client_new');
      localStorage.removeItem('tokenPair_new');
      localStorage.removeItem('openIntercom');
      localStorage.removeItem('last_route');
      localStorage.removeItem('portal_new');
    }
  }

  deleteCookie(): void {
    if (!environment.production) { // for local test - do not remove
      this._cookieService.deleteAll('/', 'abobot.loc');
    } else {
      this._cookieService.deleteAll('/', 'mentortools.com');
    }
  }

}
